import { frontendBuild } from "./buildVersion";
import { getServiceWorker } from "./utils/browserCapabilities";

const reloadKey = "sellar.client-update-attempt";
const preparationTimeout = 3_000;

const waitForUpdate = (
  registration: ServiceWorkerRegistration,
  signal: AbortSignal,
) =>
  new Promise<ServiceWorker | undefined>((resolve) => {
    const workers = new Set<ServiceWorker>();
    let checkedServer = false;
    const finish = (worker?: ServiceWorker) => {
      registration.removeEventListener("updatefound", check);
      signal.removeEventListener("abort", cancel);
      for (const observed of workers)
        observed.removeEventListener("statechange", check);
      resolve(worker);
    };
    const cancel = () => finish();
    const check = () => {
      if (signal.aborted) return finish();
      if (checkedServer && registration.waiting && !registration.installing)
        return finish(registration.waiting);
      const installing = registration.installing;
      if (installing && !workers.has(installing)) {
        workers.add(installing);
        installing.addEventListener("statechange", check);
      }
      if (checkedServer && !installing && workers.size) finish();
    };
    registration.addEventListener("updatefound", check);
    signal.addEventListener("abort", cancel, { once: true });
    // Refresh the registration before selecting a candidate: an already waiting
    // release may have been replaced or withdrawn since it downloaded.
    if (!signal.aborted) {
      void registration
        .update()
        .then(() => {
          checkedServer = true;
          check();
          if (!registration.installing && !registration.waiting) finish();
        })
        .catch(cancel);
    }
  });

const activateAtStartup = (worker: ServiceWorker) =>
  new Promise<boolean>((resolve) => {
    const channel = new MessageChannel();
    const finish = (activated: boolean) => {
      clearTimeout(timeout);
      worker.removeEventListener("statechange", onStateChange);
      channel.port1.close();
      resolve(activated);
    };
    const onStateChange = () => {
      if (worker.state === "activated") finish(true);
      if (worker.state === "redundant") finish(false);
    };
    const timeout = setTimeout(() => finish(false), 2_000);
    worker.addEventListener("statechange", onStateChange);
    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      if (event.data === "deferred") finish(false);
    };
    worker.postMessage(
      {
        type: "SELLAR_ACTIVATE_ON_STARTUP",
        release: frontendBuild?.release,
        expiresAt: Date.now() + 1_000,
      },
      [channel.port2],
    );
    onStateChange();
  });

/** Runs before React mounts; a late download must never reload an active session. */
export const updateClientAtStartup = async (): Promise<boolean> => {
  if (import.meta.env.DEV || !frontendBuild || !navigator.onLine) return false;
  try {
    const serviceWorker = getServiceWorker();
    if (!serviceWorker) return false;
    if (sessionStorage.getItem(reloadKey) === frontendBuild?.release) {
      // Consume the one-reload guard without permanently disabling future attempts.
      sessionStorage.removeItem(reloadKey);
      return false;
    }
    const signal = AbortSignal.timeout(preparationTimeout);
    const registration = await Promise.race([
      serviceWorker.getRegistration("/"),
      new Promise<undefined>((resolve) => {
        signal.addEventListener("abort", () => resolve(undefined), {
          once: true,
        });
      }),
    ]);
    if (!registration || signal.aborted) return false;
    const script = registration.active?.scriptURL;
    if (
      !script ||
      !["/sw.js", "/service-worker.js"].includes(new URL(script).pathname)
    )
      return false;
    const waiting = await waitForUpdate(registration, signal);
    if (!waiting || signal.aborted) return false;
    // Set before activation: storage failures must leave the working worker alone.
    sessionStorage.setItem(reloadKey, frontendBuild!.release);
    const activated = await activateAtStartup(waiting);
    if (!activated) {
      sessionStorage.removeItem(reloadKey);
      return false;
    }
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};
